import React, { useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSubscription } from "@/lib/useSubscription";
import { CheckCircle2, Loader2, ArrowRight, ShieldCheck, FileSignature, Download, CalendarClock, XCircle, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import SepaMandate from "./SepaMandate";

const PKG_FEATURES = {
  flexibel: [
    "85 € Grundgebühr / Monat",
    "+ 75 € pro erstelltem Gutachten",
    "Voller Funktionsumfang",
    "Mobile Erfassung & PDF-Export",
    "Auto-Schadenserkennung",
    "3 Monate Mindestlaufzeit · automatische Verlängerung um je 3 Monate",
  ],
  flatrate: [
    "850 € Grundgebühr / Monat",
    "+ 17 € Nutzungsgebühr pro Gutachten",
    "Unbegrenzte Anzahl Gutachten",
    "Sprachaufnahme & Auto-Transkription",
    "Backoffice-Workflow & Rollen",
    "DAT + Versicherungsabfrage",
    "Priorisierter Support",
    "3 Monate Mindestlaufzeit · automatische Verlängerung um je 3 Monate",
  ],
};

export default function Billing() {
  const { user, refreshMe } = useAuth();
  const sub = useSubscription();
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(null);
  const [sepaMandate, setSepaMandate] = useState(null);
  const [pendingPlan, setPendingPlan] = useState(null);   // Plan-id für Inline-Mandat
  const [cancelling, setCancelling] = useState(false);
  const sepaSectionRef = useRef(null);

  const reloadMandate = async () => {
    try {
      const { data } = await api.get("/sepa/mandate/me");
      setSepaMandate(data.mandate);
      return data.mandate;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    api.get("/billing/packages").then(({ data }) => setPackages(data.packages));
    reloadMandate();
    refreshMe();
  }, [refreshMe]);

  // Wird vom inline gerenderten <SepaMandate />-Komponenten aufgerufen,
  // sobald das Mandat erteilt + ggf. der Plan aktiviert wurde.
  const handleSepaCompleted = async (mandate) => {
    toast.success(`Mandat erteilt — ${mandate?.mandate_reference || ""}`);
    await reloadMandate();
    await refreshMe();
    setPendingPlan(null);
  };

  const [downloadingMandatePdf, setDownloadingMandatePdf] = useState(false);
  const downloadMandatePdf = async () => {
    setDownloadingMandatePdf(true);
    try {
      const res = await api.get("/sepa/mandate/me/pdf", {
        responseType: "blob",
        headers: { Accept: "application/pdf" },
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SEPA-Mandat_${sepaMandate?.mandate_reference || "Mandat"}.pdf`;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 500);
      toast.success("PDF wird heruntergeladen");
    } catch (e) {
      toast.error(apiError(e, "PDF konnte nicht erzeugt werden"));
    } finally {
      setDownloadingMandatePdf(false);
    }
  };

  /**
   * Plan wählen ⇒ kein Stripe-Checkout, sondern SEPA-Mandat-Flow.
   *
   *   - Wenn aktives Mandat vorhanden: sofort aktivieren (Backend prüft
   *     1×-pro-Monat-Limit und antwortet mit 409 + next_allowed_date,
   *     wenn schon im selben Monat gewechselt wurde).
   *   - Sonst: Banner zeigt „Bitte unten Mandat erteilen" — Formular ist
   *     bereits unter den Plänen sichtbar, kein extra Klick nötig.
   */
  const selectPlan = async (pkgId) => {
    setLoading(pkgId);
    try {
      if (sepaMandate?.status === "active") {
        await api.post("/billing/activate-plan", { plan_id: pkgId });
        toast.success(`Plan „${packages[pkgId]?.name || pkgId}" aktiviert — Mindestlaufzeit 3 Monate.`);
        await refreshMe();
        await sub.refresh();
        return;
      }
      setPendingPlan(pkgId);
      setTimeout(() => {
        sepaSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    } catch (e) {
      // Backend antwortet 409 mit { message, next_allowed_date } wenn der User
      // sein Abo noch innerhalb der 3-Monate-Bindung wechseln möchte.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof detail === "object" && detail.next_allowed_date) {
        const nice = new Date(detail.next_allowed_date).toLocaleDateString("de-DE");
        toast.error(`Plan-Wechsel während der 3-Monate-Bindung nicht möglich. Frühestens ab ${nice}.`);
      } else {
        toast.error(apiError(e));
      }
    } finally {
      setLoading(null);
    }
  };

  const handleCancel = async () => {
    if (!sub.canCancel) {
      const nice = sub.cancelAvailableFrom ? new Date(sub.cancelAvailableFrom).toLocaleDateString("de-DE") : "—";
      toast.error(`Kündigung erst nach Ablauf der 3-Monate-Bindung möglich (ab ${nice}).`);
      return;
    }
    if (!window.confirm("Wirklich das aktuelle Abo zum Ablauf der laufenden Periode kündigen?")) return;
    setCancelling(true);
    try {
      await api.post("/billing/cancel");
      toast.success("Abo gekündigt — bleibt bis zum Ende der Periode aktiv.");
      await refreshMe();
      await sub.refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof detail === "object" && detail.next_allowed_date) {
        const nice = new Date(detail.next_allowed_date).toLocaleDateString("de-DE");
        toast.error(`Kündigung erst möglich ab ${nice}.`);
      } else {
        toast.error(apiError(e));
      }
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async () => {
    setCancelling(true);
    try {
      await api.post("/billing/reactivate");
      toast.success("Abo reaktiviert — neue 3-Monate-Periode startet jetzt.");
      await refreshMe();
      await sub.refresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCancelling(false);
    }
  };

  const cancelPending = () => setPendingPlan(null);

  const active = user?.subscription_plan;
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" }) : "—";

  return (
    <Layout title="Abrechnung">
      <div className="max-w-5xl" data-testid="billing-page">
        {/* Neuer Stripe-Flow (Live-SEPA) */}
        <section className="rounded-2xl border border-sky-200 bg-sky-50 p-4 mb-6 flex items-center justify-between gap-4" data-testid="billing-stripe-cta">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-sky-700 font-bold">NEU · Stripe SEPA-Abo</div>
            <div className="text-sm text-zinc-700 mt-1">
              Zahle direkt per SEPA-Lastschrift über Stripe — automatische Rechnung, nutzungsbasierte Abrechnung pro Gutachten.
            </div>
          </div>
          <a
            href="/billing/stripe"
            className="shrink-0 inline-flex items-center h-10 px-4 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold transition"
            data-testid="billing-stripe-cta-btn"
          >
            Stripe-Abo einrichten →
          </a>
        </section>
        {/* Aktueller Plan Banner */}
        <section className="rounded-2xl brand-gradient-bg text-white p-6 mb-6" data-testid="billing-current">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold opacity-90">Aktueller Plan</div>
              <div className="font-display text-3xl font-bold mt-1">{active ? packages[active]?.name || active : "Kein aktives Abo"}</div>
              <div className="text-sm opacity-90 mt-1">
                Status: {sub.status === "active" ? "aktiv" : sub.status === "cancelled" ? "gekündigt — läuft aus" : "inaktiv"}
                {active && packages[active] && ` · ${packages[active].amount.toFixed(2)} €/${packages[active].interval}`}
              </div>
              {sub.minEndAt && (
                <div className="text-xs opacity-85 mt-2 leading-relaxed" data-testid="billing-binding-info">
                  Aktuelle Periode bis <strong>{fmtDate(sub.minEndAt)}</strong>
                  {sub.status === "active" && (
                    <> · Auto-Verlängerung um weitere {sub.minTermMonths} Monate</>
                  )}
                </div>
              )}
            </div>
            {sub.status === "active" && active && (
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={cancelling}
                className="bg-white/10 text-white border-white/30 hover:bg-white/20"
                title={sub.canCancel ? "Abo zum Ende der Periode kündigen" : `Kündigung ab ${fmtDate(sub.cancelAvailableFrom)}`}
                data-testid="billing-cancel-btn"
              >
                {cancelling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <XCircle className="h-4 w-4 mr-2" />}
                {sub.canCancel ? "Kündigen" : `Kündigung ab ${fmtDate(sub.cancelAvailableFrom)}`}
              </Button>
            )}
            {sub.status === "cancelled" && active && (
              <Button
                variant="outline"
                onClick={handleReactivate}
                disabled={cancelling}
                className="bg-white/10 text-white border-white/30 hover:bg-white/20"
                data-testid="billing-reactivate-btn"
              >
                {cancelling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCcw className="h-4 w-4 mr-2" />}
                Reaktivieren
              </Button>
            )}
          </div>
        </section>

        {/* === Plan-Cards (oben) === */}
        <h2 className="font-display text-2xl font-bold mb-5">Alle Pläne</h2>
        {sub.status === "active" && sub.minEndAt && new Date() < new Date(sub.minEndAt) && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 mb-5 flex items-start gap-3" data-testid="billing-plan-change-limit">
            <div className="h-9 w-9 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0">
              <CalendarClock className="h-4.5 w-4.5" strokeWidth={1.8} />
            </div>
            <div className="flex-1 min-w-0 text-sm text-amber-900">
              <div className="font-semibold mb-0.5">3-Monate-Mindestlaufzeit aktiv</div>
              <div className="text-xs">
                Plan-Wechsel und Kündigung sind während der laufenden Bindung nicht möglich. Frühestens ab{" "}
                <strong>{fmtDate(sub.minEndAt)}</strong> kannst du wechseln oder kündigen.
              </div>
            </div>
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-5 mb-8">
          {Object.entries(packages).map(([id, p]) => {
            const isActive = id === active && user?.subscription_status === "active";
            const bindingActive = sub.status === "active" && sub.minEndAt && new Date() < new Date(sub.minEndAt);
            const blocked = !!bindingActive && !isActive;
            return (
              <div
                key={id}
                className={`rounded-2xl border p-6 bg-white transition ${
                  isActive ? "border-emerald-300 shadow-lg" : "border-zinc-200"
                }`}
                data-testid={`billing-pkg-${id}`}
              >
                {isActive && <Badge className="bg-emerald-100 text-emerald-700 border-0 mb-2">Aktiv</Badge>}
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500">{p.name}</div>
                <div className="flex items-baseline gap-1 mt-1">
                  <span className="font-display text-4xl font-bold">{p.amount.toFixed(0)}</span>
                  <span className="text-xl text-zinc-500">€</span>
                  <span className="text-xs text-zinc-500 ml-1">/ {p.interval}</span>
                </div>
                <p className="text-sm text-zinc-500 mt-1">{p.desc}</p>
                <ul className="space-y-2 mt-5 text-sm">
                  {(PKG_FEATURES[id] || []).map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className={`w-full mt-5 ${isActive || blocked ? "" : "brand-gradient-bg text-white"}`}
                  variant={isActive || blocked ? "outline" : "default"}
                  onClick={() => selectPlan(id)}
                  disabled={loading === id || isActive || blocked}
                  data-testid={`billing-cta-${id}`}
                >
                  {loading === id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> :
                    blocked ? <CalendarClock className="h-4 w-4 mr-2" /> :
                    (sepaMandate?.status === "active"
                      ? <ArrowRight className="h-4 w-4 mr-2" />
                      : <FileSignature className="h-4 w-4 mr-2" />)}
                  {isActive ? "Aktueller Plan" :
                    blocked ? `Ab ${new Date(sub.minEndAt).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}.` :
                    (sepaMandate?.status === "active" ? "Diesen Plan wählen" : "Plan per SEPA aktivieren")}
                </Button>
                {!isActive && !blocked && (!sepaMandate || sepaMandate.status !== "active") && (
                  <div className="text-[11px] text-zinc-500 mt-2 text-center leading-tight">
                    Lastschrift — keine Kreditkarte nötig
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* === SEPA-Mandat-Section (unter den Plänen) === */}
        <section ref={sepaSectionRef} data-testid="billing-sepa-section">
          {/* Status-Card erscheint NUR nach erfolgreichem Submit (aktives Mandat) */}
          {sepaMandate?.status === "active" && (
            <div
              className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5 mb-4 flex items-start gap-4"
              data-testid="billing-sepa-card"
            >
              <div className="h-11 w-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-emerald-700">SEPA-Lastschriftmandat</div>
                <div className="font-display font-semibold text-base mt-0.5">
                  Mandat erteilt — {sepaMandate.mandate_reference}
                </div>
                <div className="text-xs text-zinc-600 mt-0.5">
                  Erteilt am {new Date(sepaMandate.signed_at).toLocaleDateString("de-DE")} ·
                  IBAN endet auf {sepaMandate.iban?.slice(-4)} ·
                  Gläubiger-ID {sepaMandate.creditor_id}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <Button
                  onClick={downloadMandatePdf}
                  disabled={downloadingMandatePdf}
                  className="brand-gradient-bg text-white"
                  data-testid="billing-sepa-pdf"
                >
                  {downloadingMandatePdf
                    ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    : <Download className="h-4 w-4 mr-1.5" />}
                  PDF herunterladen
                </Button>
                <Link to="/billing/sepa">
                  <Button variant="outline" data-testid="billing-sepa-cta">
                    Anzeigen <ArrowRight className="h-4 w-4 ml-1.5" />
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Inline SEPA-Formular — IMMER sichtbar, solange kein aktives Mandat */}
          {(!sepaMandate || sepaMandate.status !== "active") && (
            <div className="rounded-2xl border-2 border-cyan-300 bg-white overflow-hidden shadow-lg" data-testid="billing-sepa-inline">
              <div className="px-4 py-2.5 border-b border-zinc-100 bg-zinc-50/60">
                <div className="text-xs font-semibold text-zinc-700">
                  {pendingPlan
                    ? `SEPA-Mandat erteilen für Tarif „${packages[pendingPlan]?.name}"`
                    : "SEPA-Lastschriftmandat erteilen"}
                </div>
              </div>
              <div className="p-3 sm:p-5">
                <SepaMandate
                  plan={pendingPlan}
                  embedded
                  onCompleted={handleSepaCompleted}
                />
              </div>
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
